import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Heart, Flame, ArrowRight } from "lucide-react";

const METHOD_IMG = {
  v60: "https://images.unsplash.com/photo-1545665225-b23b99e4d45e?auto=format&fit=crop&w=900&q=80",
  chemex: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?auto=format&fit=crop&w=900&q=80",
  espresso: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=900&q=80",
  french_press: "https://images.unsplash.com/photo-1485808191679-5f86510681a2?auto=format&fit=crop&w=900&q=80",
  aeropress: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=900&q=80",
  moka: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?auto=format&fit=crop&w=900&q=80",
  cold_brew: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?auto=format&fit=crop&w=900&q=80",
  turkish: "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=900&q=80",
};
const imageFor = (r) => r?.image_url || METHOD_IMG[(r?.method || "").toLowerCase()] || null;

export default function TrendingRecipes() {
  const { lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const { data: recipesData } = useQuery({
    queryKey: ["recipes"],
    queryFn: endpoints.recipes,
  });
  const { data: countMap = {} } = useQuery({
    queryKey: ["favoriteCounts"],
    queryFn: endpoints.favoriteCounts,
  });

  const all = React.useMemo(() => {
    const c = recipesData?.curated || [];
    const co = recipesData?.community || [];
    return [...c, ...co];
  }, [recipesData]);

  // Sort by favorite count desc, fallback to created_at desc; pick top 6 with at least 1 favorite OR fall back to first 6 curated
  const top = React.useMemo(() => {
    const withCounts = all.map((r) => ({ ...r, favCount: countMap[r.id] || 0 }));
    const hasFavs = withCounts.some((r) => r.favCount > 0);
    if (hasFavs) {
      return withCounts
        .filter((r) => r.favCount > 0)
        .sort((a, b) => b.favCount - a.favCount)
        .slice(0, 6);
    }
    // Fallback: pick 3 curated to seed the section visually before any favorites exist
    return (recipesData?.curated || []).slice(0, 3).map((r) => ({ ...r, favCount: 0 }));
  }, [all, countMap, recipesData]);

  if (top.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24" data-testid="trending-section">
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">
            <Flame className="w-3.5 h-3.5" />
            {isRTL ? "الأكثر تفضيلاً" : "Most loved"}
          </div>
          <h2 className={`${serifClass} text-3xl lg:text-5xl text-[#2A2421] max-w-2xl leading-[1.05]`}>
            {isRTL ? "وصفات يحبّها المجتمع" : "Recipes the community loves"}
          </h2>
        </div>
        <Link
          to="/recipes"
          className="hidden sm:flex items-center gap-2 text-sm text-[#C86240] hover:underline"
          data-testid="trending-see-all"
        >
          {isRTL ? "الكل" : "See all"} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {top.map((r) => (
          <Link
            key={r.id}
            to="/recipes"
            data-testid={`trending-card-${r.id}`}
            className="group soft-card rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform relative"
          >
            {r.favCount > 0 && (
              <div
                className="absolute top-3 end-3 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 shadow-sm"
                data-testid={`fav-badge-${r.id}`}
              >
                <Heart className="w-3.5 h-3.5 text-[#C86240] fill-[#C86240]" />
                <span className="text-xs font-semibold text-[#2A2421]">{r.favCount}</span>
              </div>
            )}
            <div className="aspect-[4/3] bg-[#F9F6F0] overflow-hidden">
              {imageFor(r) && (
                <img src={imageFor(r)} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
              )}
            </div>
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#4A5D4E] mb-2">
                {r.method} · {r.is_community ? (isRTL ? "مجتمع" : "Community") : (isRTL ? "منتقاة" : "Curated")}
              </div>
              <div className={`${serifClass} text-xl text-[#2A2421] leading-tight`}>
                {lang === "ar" ? r.title_ar : r.title_en}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
